import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronsLeft, Menu, LayoutDashboard, Users, Route, Contact, Settings, ArrowRightLeft, BarChart3, User, ShieldCheck, Package } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

// Mapeo de rutas a módulos de permisos
const RUTA_A_MODULO = {
  '/': 'dashboard',
  '/voluntarios': 'voluntarios',
  '/rutas': 'rutas',
  '/afectados': 'afectados',
  '/afectados/registro': 'afectados',
  '/afectados/lista': 'afectados',
  '/afectados/solicitudes': 'afectados',
  '/afectados/seguimiento': 'afectados',
  '/afectados/configuracion': 'configuracion',
  '/afectados/:id': 'afectados',
  '/inventario': 'inventario',
  '/inventario/categorias': 'inventario_categorias',
  '/inventario/insumos': 'inventario_insumos',
  '/inventario/kits': 'inventario_insumos',
  '/inventario/centros': 'inventario_centros',
  '/inventario/ingresos': 'inventario_ingresos',
  '/inventario/salidas': 'inventario_salidas',
  '/inventario/donaciones': 'inventario_donaciones',
  '/inventario/entregas': 'entregas',
  '/inventario/traslados': 'inventario_traslados',
  '/inventario/ajustes': 'inventario_ajustes',
  '/inventario/existencias': 'inventario_existencias',
  '/inventario/kardex': 'inventario_kardex',
  '/configuracion/permisos': 'permisos',
}

// Items del menú con su módulo asociado
const menuGroups = [
  {
    label: 'General',
    modulo: null,
    items: [
      { path: '/', label: 'Dashboard', icon: <LayoutDashboard size={18} />, modulo: 'dashboard' },
      { path: '/voluntarios', label: 'Voluntarios', icon: <Users size={18} />, modulo: 'voluntarios' },
      { path: '/rutas', label: 'Rutas', icon: <Route size={18} />, modulo: 'rutas' },
    ],
    submenuAfectados: {
      modulo: 'afectados',
      title: 'Afectados',
      icon: <Contact size={18} />,
      paths: [
        { path: '/afectados', label: 'Dashboard', modulo: 'afectados' },
        { path: '/afectados/registro', label: 'Registro', modulo: 'afectados' },
        { path: '/afectados/lista', label: 'Lista', modulo: 'afectados' },
        { path: '/afectados/solicitudes', label: 'Solicitudes', modulo: 'afectados' },
        { path: '/afectados/seguimiento', label: 'Seguimiento', modulo: 'afectados' },
        { path: '/afectados/configuracion', label: 'Configuración', modulo: 'configuracion' },
      ]
    }
  },
  {
    label: 'Inventario',
    modulo: null,
    items: [],
    submenu: [
      { title: 'Dashboard', icon: <LayoutDashboard size={18} />, modulo: 'inventario', paths: [{ path: '/inventario', label: 'Dashboard', modulo: 'inventario' }] },
      { title: 'Configuracion', icon: <Settings size={18} />, modulo: null, paths: [
        { path: '/inventario/categorias', label: 'Categorias', modulo: 'inventario_categorias' },
        { path: '/inventario/insumos', label: 'Insumos', modulo: 'inventario_insumos' },
        { path: '/inventario/kits', label: 'Kits', modulo: 'inventario_insumos' },
        { path: '/inventario/centros', label: 'Centros', modulo: 'inventario_centros' },
      ]},
      { title: 'Movimientos', icon: <ArrowRightLeft size={18} />, modulo: null, paths: [
        { path: '/inventario/ingresos', label: 'Ingresos', modulo: 'inventario_ingresos' },
        { path: '/inventario/salidas', label: 'Salidas', modulo: 'inventario_salidas' },
        { path: '/inventario/donaciones', label: 'Recibir Donación', modulo: 'inventario_donaciones' },
        { path: '/inventario/entregas', label: 'Hacer Donación', modulo: 'entregas' },
        { path: '/inventario/traslados', label: 'Traslados', modulo: 'inventario_traslados' },
        { path: '/inventario/ajustes', label: 'Ajustes', modulo: 'inventario_ajustes' },
      ]},
      { title: 'Reportes', icon: <BarChart3 size={18} />, modulo: null, paths: [
        { path: '/inventario/existencias', label: 'Existencias', modulo: 'inventario_existencias' },
        { path: '/inventario/kardex', label: 'Kardex', modulo: 'inventario_kardex' },
      ]},
    ]
  },
  {
    label: 'Otros',
    modulo: null,
    items: [
      { path: '/perfil', label: 'Perfil', icon: <User size={18} />, modulo: null },
      { path: '/configuracion/permisos', label: 'Permisos', icon: <ShieldCheck size={18} />, modulo: 'permisos' },
    ]
  }
]

const Sidebar = ({ oculto = false, onToggleOculto }) => {
  const location = useLocation()
  const { user, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [submenusOpen, setSubmenusOpen] = useState({})
  const [permisos, setPermisos] = useState([])

  useEffect(() => {
    const cargarPermisos = async () => {
      if (!user) return
      const { data: voluntario } = await supabase
        .from('voluntarios')
        .select('rol')
        .eq('auth_id', user.id)
        .single()
      if (!voluntario) return
      // Admin tiene todos los permisos
      if (voluntario.rol === 'admin') {
        setPermisos(['*'])
        return
      }
      const { data } = await supabase
        .from('permisos_roles')
        .select('modulo')
        .eq('rol', voluntario.rol)
        .eq('permiso', 'ver')
        .eq('activo', true)
      setPermisos(data?.map(p => p.modulo) || [])
    }
    cargarPermisos()
  }, [user])

  const tienePermiso = (modulo) => {
    if (!modulo) return true // Items sin módulo siempre visibles (perfil)
    if (permisos.includes('*')) return true // Admin ve todo
    return permisos.includes(modulo)
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const toggleSubmenu = (key) => {
    setSubmenusOpen(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleNavClick = () => setMobileOpen(false)

  const isAnyChildActive = (group) => {
    if (!group.submenu) return false
    for (const section of group.submenu)
      for (const p of section.paths)
        if (location.pathname.startsWith(p.path)) return true
    return false
  }

  return (
    <>
      <button className="sidebar-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" style={{
        display: 'none', position: 'fixed', top: '10px', left: '10px', zIndex: 1100,
        background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
        padding: '8px', cursor: 'pointer', color: '#374151', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {mobileOpen ? (
            <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
          ) : (
            <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
          )}
        </svg>
      </button>

      {/* Botón para reabrir el sidebar en escritorio cuando está oculto */}
      {oculto && (
        <button className="sidebar-reabrir" onClick={onToggleOculto} aria-label="Mostrar menú" title="Mostrar menú">
          <Menu size={22} />
        </button>
      )}

      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{
          display: 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 998
        }} />
      )}

      <aside className={`sidebar ${mobileOpen ? 'open' : ''} ${oculto ? 'oculto' : ''}`}>
        <div className="sidebar-brand">
          <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🆘</span>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#DC2626' }}>R. Solidaridad</div>
            <div style={{ fontSize: '0.6rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gestion de Ayuda</div>
          </div>
          <button className="sidebar-ocultar" onClick={onToggleOculto} aria-label="Ocultar menú" title="Ocultar menú">
            <ChevronsLeft size={18} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuGroups.map((group, gi) => (
            <div key={gi} className="sidebar-group">
              <div className="sidebar-group-label">{group.label}</div>

              {/* Items simples filtrados */}
              {group.items.filter(item => tienePermiso(item.modulo)).map((item) => (
                <Link key={item.path} to={item.path} onClick={handleNavClick}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}>
                  <span className="sidebar-icon">{item.icon}</span>
                  <span className="sidebar-label">{item.label}</span>
                </Link>
              ))}

              {/* Submenu Afectados (filtrado por permiso) */}
              {group.submenuAfectados && tienePermiso(group.submenuAfectados.modulo) && (
                <div className="sidebar-submenu">
                  <button className={`sidebar-submenu-toggle ${location.pathname.startsWith('/afectados') ? 'active' : ''}`}
                    onClick={() => toggleSubmenu(`af${gi}`)}>
                    <span className="sidebar-icon">{group.submenuAfectados.icon}</span>
                    <span className="sidebar-label">{group.submenuAfectados.title}</span>
                    <svg className={`arrow ${submenusOpen[`af${gi}`] || location.pathname.startsWith('/afectados') ? 'open' : ''}`}
                      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className={`submenu-content ${submenusOpen[`af${gi}`] || location.pathname.startsWith('/afectados') ? 'open' : ''}`}>
                    {group.submenuAfectados.paths.filter(p => tienePermiso(p.modulo)).map(p => (
                      <Link key={p.path} to={p.path} onClick={handleNavClick}
                        className={`sidebar-link sub ${location.pathname === p.path ? 'active' : ''}`}>
                        <span className="sidebar-bullet">•</span>
                        <span>{p.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Submenu Inventario (filtrado por permiso de cada ruta) */}
              {group.submenu && (
                <div className="sidebar-submenu">
                  <button className={`sidebar-submenu-toggle ${isAnyChildActive(group) ? 'active' : ''}`}
                    onClick={() => toggleSubmenu(`g${gi}`)}>
                    <span className="sidebar-icon"><Package size={18} /></span>
                    <span className="sidebar-label">Inventario</span>
                    <svg className={`arrow ${submenusOpen[`g${gi}`] || isAnyChildActive(group) ? 'open' : ''}`}
                      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div className={`submenu-content ${submenusOpen[`g${gi}`] || isAnyChildActive(group) ? 'open' : ''}`}>
                    {group.submenu.map((section, si) => {
                      const pathsVisibles = section.paths.filter(p => tienePermiso(p.modulo))
                      if (pathsVisibles.length === 0) return null
                      return (
                        <div key={si}>
                          <div className="submenu-section-label">{section.icon} {section.title}</div>
                          {pathsVisibles.map(p => (
                            <Link key={p.path} to={p.path} onClick={handleNavClick}
                              className={`sidebar-link sub ${location.pathname === p.path ? 'active' : ''}`}>
                              <span className="sidebar-bullet">•</span>
                              <span>{p.label}</span>
                            </Link>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{user?.email?.charAt(0).toUpperCase() || 'U'}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user?.email?.split('@')[0] || 'Usuario'}</span>
              <span className="sidebar-user-email">{user?.email || ''}</span>
            </div>
          </div>
          <button onClick={logout} className="sidebar-logout" title="Cerrar sesion">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </aside>

      <style>{`
        .sidebar-hamburger { display: none !important; }
        .sidebar { width: 220px; position: sticky; top: 0; height: 100vh; min-height: 100vh; background: #fff; border-right: 1px solid #f0f0f0; display: flex; flex-direction: column; flex-shrink: 0; transition: transform .2s, width .2s; font-size: .85rem; }
        .sidebar-brand { display: flex; align-items: center; gap: 8px; padding: 14px 14px 12px; border-bottom: 1px solid #f0f0f0; }
        .sidebar-nav { flex: 1; padding: 6px 6px; overflow-y: auto; }
        .sidebar-group { margin-bottom: 2px; }
        .sidebar-group-label { font-size: .6rem; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: .08em; padding: 6px 8px 3px; }
        .sidebar-link { display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 6px; font-size: .82rem; font-weight: 500; color: #6B7280; text-decoration: none; transition: all .12s; }
        .sidebar-link:hover { background: #F9FAFB; color: #374151; }
        .sidebar-link.active { background: #FEE2E2; color: #DC2626; font-weight: 600; }
        .sidebar-icon { font-size: .9rem; width: 20px; text-align: center; flex-shrink: 0; }
        .sidebar-label { white-space: nowrap; }
        .sidebar-submenu-toggle { display: flex; align-items: center; gap: 8px; width: 100%; padding: 7px 8px; border: none; border-radius: 6px; font-size: .82rem; font-weight: 500; font-family: 'Inter',sans-serif; color: #6B7280; background: none; cursor: pointer; }
        .sidebar-submenu-toggle:hover { background: #F9FAFB; color: #374151; }
        .sidebar-submenu-toggle.active { color: #DC2626; font-weight: 600; }
        .arrow { margin-left: auto; transition: transform .2s; }
        .arrow.open { transform: rotate(180deg); }
        .submenu-content { max-height: 0; overflow: hidden; transition: max-height .3s; }
        .submenu-content.open { max-height: 500px; }
        .submenu-section-label { font-size: .65rem; font-weight: 600; color: #9CA3AF; padding: 4px 8px 2px 18px; }
        .sidebar-link.sub { padding: 5px 8px 5px 26px; font-size: .78rem; }
        .sidebar-bullet { font-size: .55rem; color: #D1D5DB; width: 14px; text-align: center; flex-shrink: 0; }
        .sidebar-link.sub.active .sidebar-bullet { color: #DC2626; }

        .sidebar-footer { padding: 10px 12px; border-top: 1px solid #f0f0f0; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
        .sidebar-user { display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; }
        .sidebar-avatar { width: 28px; height: 28px; border-radius: 6px; background: #DC2626; color: white; display: flex; align-items: center; justify-content: center; font-size: .75rem; font-weight: 600; flex-shrink: 0; }
        .sidebar-user-info { display: flex; flex-direction: column; min-width: 0; }
        .sidebar-user-name { font-size: .75rem; font-weight: 600; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sidebar-user-email { font-size: .62rem; color: #9CA3AF; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sidebar-logout { background: none; border: none; cursor: pointer; color: #9CA3AF; padding: 4px; border-radius: 4px; flex-shrink: 0; }
        .sidebar-logout:hover { color: #DC2626; background: #FEE2E2; }

        .sidebar-ocultar { margin-left: auto; background: none; border: none; cursor: pointer; color: #9CA3AF; padding: 4px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .sidebar-ocultar:hover { color: #374151; background: #F3F4F6; }
        .sidebar-reabrir { display: none; position: fixed; top: 10px; left: 10px; z-index: 1100; background: white; border: 1px solid #E5E7EB; border-radius: 8px; padding: 8px; cursor: pointer; color: #374151; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
        .sidebar-reabrir:hover { color: #DC2626; }

        @media (min-width: 769px) {
          .sidebar { position: sticky; top: 0; z-index: 10; }
          .sidebar.oculto { width: 0; min-width: 0; border-right: none; overflow: hidden; }
          .sidebar-reabrir { display: flex; align-items: center; justify-content: center; }
        }
        @media (max-width: 768px) {
          .sidebar-hamburger { display: flex !important; }
          .sidebar { position: fixed; top: 0; left: 0; z-index: 999; width: 270px; height: 100vh; transform: translateX(-100%); box-shadow: 4px 0 20px rgba(0,0,0,0.1); }
          .sidebar.open { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}

export default Sidebar